import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EDayType } from 'projects/shared/lib/enum/time/day-type.enum';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { IAddressServiceToken } from 'projects/shared/lib/services/data/address/address-service.interface';
import { IBsService, IBsServiceToken } from 'projects/shared/lib/services/data/bs/bs-service.interface';
import { ICalendarService, ICalendarServiceToken } from 'projects/shared/lib/services/data/calendar/calendar-service.interface';
import { IComparisonService, IComparisonServiceToken } from 'projects/shared/lib/services/infrastructure/comparison/comparison-service.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { AddressDto, AddresstourDto, BsDto, CalendarDto, DayOfWeek as EDayOfWeek, ShoppingCartDto } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { catchError, firstValueFrom, of, take, tap } from 'rxjs';
import { IBetriebsstaetteEnvironment } from 'shared/interfaces/environment/checkout/betriebsstaette-environment.interface';

@Component({
  selector: 'app-cart-creation-dialog-base',
  template: '',
  standalone: false
})
export class CartCreationDialogBaseComponent implements OnInit {
  private readonly destroyRef: DestroyRef;
  @Input() public cart?: ShoppingCartDto | undefined;
  @Input() public isCartCreation: boolean | undefined;
  @Output() modalCancel: EventEmitter<void> = new EventEmitter();
  @Output() cartCreateClicked: EventEmitter<ShoppingCartDto> = new EventEmitter();

  protected readonly logger: LoggerService;
  protected readonly calendar: ICalendarService;
  protected readonly comparisonService: IComparisonService;

  protected readonly bsService?: IBsService | null | undefined;

  public addressList: AddressDto[] | undefined;
  public addressSelected?: AddressDto | undefined;
  public addressSelectedBackup?: AddressDto | undefined;

  public yearSelected!: number; // NO offset
  public monthSelected!: number; // OFFSET 1 required for handling without Date object! [0..11]
  public dateSelected?: Date | undefined;

  public dayColumnsToDisplay: EDayOfWeek[];

  public customerOrderNumber: string | undefined;
  public comment: string | undefined;

  public calendarDayList: (CalendarDayModel | undefined)[] = [];

  private readonly appendDaysOfNeighbouringMonths: boolean = false;
  public readonly displayEmptyCellsForDaysOfNeighbouringMonths: boolean = false;

  private readonly serverTimeZone = 1; // GMT+1
  public hotline: string | undefined;
  private bs: BsDto | undefined;
  private bsConfig: IBetriebsstaetteEnvironment | undefined;
  private bsConfigLoading?: boolean | undefined;

  constructor(
  ) {
    this.destroyRef = inject(DestroyRef);
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.calendar = inject(ICalendarServiceToken);
    this.comparisonService = inject(IComparisonServiceToken);
    this.bsService = inject(IBsServiceToken, { optional: true });

    this.yearSelected = new Date().getFullYear();
    this.monthSelected = new Date().getMonth();

    this.dayColumnsToDisplay = [
      EDayOfWeek.Monday,
      EDayOfWeek.Tuesday,
      EDayOfWeek.Wednesday,
      EDayOfWeek.Thursday,
      EDayOfWeek.Friday,
    ];  // Week starts with "Sunday"(0) > "Monday"(1) ... > "Saturday"(6) (current implementation doesnt allow start with "Monday" and appending "Sunday" at the end.)
  }

  public async ngOnInit(): Promise<void> {
    this.bsLoad();
    await this.addressListLoadAsync();

    if (this.cart == undefined) {
      this.cart = new ShoppingCartDto();
    } else {
      this.addressSelectedUpdate(this.cart.deliveryAddressData, this.addressList);
      let deliveryDate = this.cart.deliveryDate ? new Date(this.cart.deliveryDate) : undefined;
      deliveryDate?.setUTCHours(12, 0, 0, 0);
      this.logger.info(`ngOnInit deliveryDate: ${deliveryDate?.toString()}`, this.constructor.name);
      if (deliveryDate != undefined) {
        this.dateSelected = deliveryDate;
        this.logger.info(`ngOnInit dateSelected: ${this.dateSelected.toString()}`, this.constructor.name);
        this.yearSelected = deliveryDate.getFullYear();
        this.logger.info(`ngOnInit yearSelected: ${this.yearSelected}`, this.constructor.name);
        this.monthSelected = deliveryDate.getMonth();
        this.logger.info(`ngOnInit monthSelected: ${this.monthSelected}`, this.constructor.name);
      }
      this.customerOrderNumber = this.cart.customerOrderNumber;
      this.comment = this.cart.comment;
    }

    this.calendarListGet();
  }

  private bsLoad() {
    if (this.bsService == undefined) { return; }
    this.bsConfigLoading = true;
    this.bsService?.bsGet().pipe(
      takeUntilDestroyed(this.destroyRef),
      take(1),
      tap(
        (bs: BsDto | undefined) => {
          this.hotline = bs?.mobile;
          this.bs = bs;
          this.bsConfigUpdate(this.bs?.nr);
          this.bsConfigLoading = false;
          this.calendarListGet();
        }
      ),
      catchError((err) => {
        this.bsConfigLoading = false;
        return of(undefined);
      })
    ).subscribe();
  }

  private bsConfigUpdate(bsNumber?: number | undefined) {
    if (bsNumber == undefined) {
      this.bsConfig = undefined;
      return;
    }

    let checkoutConfiguration = ENVIRONMENT.checkout;
    this.bsConfig = checkoutConfiguration.betriebsstaette?.find((bs) => bs.number == bsNumber, undefined);
    if (this.bsConfig == undefined) {
      this.bsConfig = {
        number: bsNumber,
        defaultCommissionTime: checkoutConfiguration.defaultCommissionTime,
        defaultCommissionDayOffset: checkoutConfiguration.defaultCommissionDayOffset,
        commissiondayScheduleList: []
      };
    }

    if (this.bsConfig.commissiondayScheduleList == undefined) {
      this.bsConfig.commissiondayScheduleList = [];
    }

    let weekDayList = Object.keys(EDayOfWeek).filter((e) => isNaN(Number(e))).map((s) => EDayOfWeek[s as keyof typeof EDayOfWeek]);
    weekDayList.forEach((weekday, index) => {
      let bsConfigDay = this.bsConfig?.commissiondayScheduleList?.find((d) => d.day == weekday);
      if (bsConfigDay == undefined) {
        bsConfigDay = {
          day: weekday,
          time: {
            hour: this.bsConfig?.defaultCommissionTime?.hour as number,
            minute: this.bsConfig?.defaultCommissionTime?.minute as number,
          },
          dayOffset: this.bsConfig?.defaultCommissionDayOffset
        };
        this.bsConfig?.commissiondayScheduleList?.push(bsConfigDay);
      }
    });
  }

  public cartCreateClick() {
    if (this.cart != undefined) {
      if (this.cart.deliveryDate != undefined) {
        this.logger.info(`cartCreateClick deliveryDate: ${this.cart.deliveryDate?.toString()}`, this.constructor.name);
        let deliveryDate = new Date(this.cart.deliveryDate);
        deliveryDate?.setUTCHours(12, 0, 0, 0);
        this.cart.deliveryDate = new Date(deliveryDate);
      }
      this.cartCreateClicked.emit(this.cart);
    }
  }

  private dateSelectedUpdate(date?: Date | undefined) {
    this.calendarDayList.forEach(d => d ? d.isSelected = false : undefined);

    if (date != undefined) {

      let dayIsInTourList = this.dayIsInTourList(this.addressSelected?.tourList, date.getDay());
      if (this.cart != undefined) {
        if (dayIsInTourList) {
          this.dateSelected = new Date(date);
          this.dateSelected.setUTCHours(12, 0, 0, 0);
          this.cart.deliveryDate = new Date(this.dateSelected);
        } else {
          this.dateSelected = undefined;
          this.cart.deliveryDate = undefined;
        }
      }
      // }

      // let dateChanged = !this.comparisonService.equalsDateByValues(this.dateSelected, date);
      // // this.logger.log(`dateSelectedUpdate dateChanged: ${dateChanged}`);
      // this.calendarDayList.forEach(d => d ? d.isSelected = false : undefined);

      // let previousDaySelected = this.calendarDayList.find(d => this.comparisonService.equalsDateByValues(d?.date, date));
      // this.logger.log(`dateSelectedUpdate previousDaySelected: ${previousDaySelected?.date.toString()}`);
      // if (previousDaySelected != undefined) {
      //   previousDaySelected.isSelected = false;
      // }

      // if (date) {
      let monthDiffers = date.getMonth() != this.monthSelected;
      this.logger.info(`dateSelectedUpdate monthDiffers: ${monthDiffers}`, this.constructor.name);

      // this.dateSelected = date;
      // this.logger.log(`dateSelectedUpdate dateSelected: ${this.dateSelected.toString()}`);
      this.yearSelected = date?.getFullYear();
      this.logger.info(`dateSelectedUpdate yearSelected: ${this.yearSelected}`, this.constructor.name);
      this.monthSelected = date?.getMonth();
      this.logger.info(`dateSelectedUpdate monthSelected: ${this.monthSelected}`, this.constructor.name);

      if (monthDiffers) {
        this.calendarListGet();
      } else {
        if (this.dateSelected != undefined) { // && dayIsInTourList
          let newDaySelected = this.calendarDayList.find(d => this.comparisonService.equalsDateByValues(d?.date, this.dateSelected));
          if (newDaySelected != undefined) {
            newDaySelected.isSelected = true;
          }
        }
      }

    } else {
      this.dateSelected = undefined;

      if (this.cart != undefined) {
        this.cart.deliveryDate = undefined;
      }
    }
  }

  private addressSelectedUpdate(cartDeliveryAddress: AddressDto | undefined, addressList: AddressDto[] | undefined) {
    if (cartDeliveryAddress == undefined || addressList == undefined) {
      return;
    }

    let matchingAddress = addressList.find(a => this.comparisonService.equalsAddressIdentifierByNumberAndType(cartDeliveryAddress.addressIdentifier, a.addressIdentifier));
    if (matchingAddress != this.addressSelected) {
      this.addressSelectedBackup = this.addressSelected;
      this.addressSelected = matchingAddress;
    }
  }

  private async addressListLoadAsync() {
    await firstValueFrom(ServiceLocator.injector.get(IAddressServiceToken).addressListGet()).then((result) => {
      this.addressList = result.addressList?.filter(a => {
        let toursExist = a.tourList != undefined && a.tourList.length > 0;
        return toursExist;
      });
      if (this.addressList != undefined && this.addressList?.length > 0) {
        this.addressSelected = this.addressList[0];
      }
      this.addressSelectedUpdate(this.cart?.deliveryAddressData, this.addressList);
    }).catch((error) => {
      this.logger.warn(error, this.constructor.name);
      let errorMessage = $localize`:@@CartCreationDialogAddressLoadErrorMessage:Could not load addresses`;
      throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
    });
  }

  public close(event: MouseEvent) {
    this.modalCancel.emit();
  }

  public monthDecrement() {
    let monthYearSelected = new Date(new Date(this.yearSelected, this.monthSelected).setMonth(this.monthSelected - 1));
    this.monthSelected = monthYearSelected.getMonth();
    this.yearSelected = monthYearSelected.getFullYear();
    this.calendarListGet();
  }

  public monthIncrement() {
    let monthYearSelected = new Date(new Date(this.yearSelected, this.monthSelected).setMonth(this.monthSelected + 1));
    this.monthSelected = monthYearSelected.getMonth();
    this.yearSelected = monthYearSelected.getFullYear();
    this.calendarListGet();
  }

  public daySelect(date: Date | undefined) {
    if (this.comparisonService.equalsDateByValues(this.dateSelected, date)) {
      return;
    }

    this.dateSelectedUpdate(date);
  }

  private dayIsInTourList(tourList: AddresstourDto[] | undefined, day: number | undefined) {
    return tourList?.find(t => t.tourDay == day) != undefined;
  }

  public addressToCompactStringGet(address: AddressDto) {
    let addressString = `${address.street}, ${address.countryCode}-${address.zipCode} ${address.city}`;
    return addressString;
  }

  public async deliveryAddressChangedAsync() {
    if (this.cart?.deliveryAddressData != undefined) {
      this.cart.deliveryAddressData = this.addressSelected;
    }

    this.dateSelectedUpdate(this.dateSelected);
    this.calendarListGet();
  }

  public customerOrderNumberChanged() {
    if (this.cart == undefined) {
      this.logger.warn("No cart in cart creation dialog upon saving customerOrderNumber changes.", this.constructor.name);
      return;
    }

    this.cart.customerOrderNumber = this.customerOrderNumber?.trim();
  }

  public commentChanged() {
    if (this.cart == undefined) {
      this.logger.warn("No cart in cart creation dialog upon saving comment changes.", this.constructor.name);
      return;
    }

    this.cart.comment = this.comment?.trim();
  }

  private calendarListGet(): void {
    if (this.addressSelected?.countryCode == undefined) {
      return;
    }

    if (this.bsConfigLoading) {
      return;
    }

    this.calendarDayList = [];

    let fromDate: Date;
    let toDate: Date;

    let firstDateOfMonth = new Date(this.yearSelected, this.monthSelected, 1, 12);
    firstDateOfMonth.setUTCHours(12, 0, 0, 0);
    this.logger.info(`calendarListGet firstDateOfMonth: ${firstDateOfMonth.toString()}`, this.constructor.name);
    let startDateDayOffset = 0;
    this.logger.info(`calendarListGet startDateDayOffset: ${startDateDayOffset}`, this.constructor.name);

    if (firstDateOfMonth.getDay() > 1 && firstDateOfMonth.getDay() < 6) {
      startDateDayOffset = firstDateOfMonth.getDay() - 1;
      this.logger.info(`calendarListGet startDateDayOffset changed: ${startDateDayOffset}`, this.constructor.name);
    }

    let currentDate = new Date(firstDateOfMonth);
    currentDate.setUTCHours(12, 0, 0, 0);
    this.logger.info(`calendarListGet currentDate: ${currentDate.toString()}`, this.constructor.name);

    fromDate = currentDate;

    // prefill with days of previous month -> start found
    if (this.appendDaysOfNeighbouringMonths) {
      currentDate.setDate(currentDate.getDate() - startDateDayOffset);
      fromDate = currentDate;
    } else {
      for (let index = 0; index < startDateDayOffset; index++) {
        this.calendarDayList.push(undefined);
      }
    }

    // fill grid/list untill end of this month reached
    while (currentDate.getMonth() <= this.monthSelected && currentDate.getFullYear() == this.yearSelected) {
      currentDate.setUTCHours(12, 0, 0, 0);
      if (currentDate.getDay() == 0 || currentDate.getDay() == 6) {
        currentDate = new Date(currentDate);
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      let dateIsSelected = this.comparisonService.equalsDateByValues(currentDate, this.dateSelected);

      let calendarDay = new CalendarDayModel(
        currentDate,
        currentDate.getDate(),
        currentDate.getDay(),
        dateIsSelected);
      this.calendarDayList.push(calendarDay);
      this.logger.info(`calendarListGet calendarDayList push: ${calendarDay.date.toString()}`, this.constructor.name);

      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // append days of  following month for a complete grid
    let pendingEmptyCellCount = this.dayColumnsToDisplay.length - (this.calendarDayList.length % this.dayColumnsToDisplay.length);
    if (pendingEmptyCellCount == this.dayColumnsToDisplay.length) { pendingEmptyCellCount = 0; }

    for (let index = 0; index < pendingEmptyCellCount; index++) {

      if (this.appendDaysOfNeighbouringMonths) {
        currentDate.setUTCHours(12, 0, 0, 0);
        if (currentDate.getDay() == 0 || currentDate.getDay() == 6) {
          currentDate = new Date(currentDate);
          currentDate.setDate(currentDate.getDate() + 1);
          continue;
        }

        let dateIsSelected = this.comparisonService.equalsDateByValues(currentDate, this.dateSelected);

        let calendarDay = new CalendarDayModel(
          currentDate,
          currentDate.getDate(),
          currentDate.getDay(),
          dateIsSelected);
        this.calendarDayList.push(calendarDay);
        this.logger.info(`calendarListGet calendarDayList push: ${calendarDay.date.toString()}`, this.constructor.name);

        currentDate = new Date(currentDate);
        currentDate.setDate(currentDate.getDate() + 1);
      } else {
        this.calendarDayList.push(undefined);
      }
    }

    // update isAvailable State
    toDate = currentDate;
    let now = new Date();

    let numberOfDaysIntoPast = 14;
    fromDate = new Date(fromDate.getTime() - (numberOfDaysIntoPast * 24 * 60 * 60 * 1000));

    let numberOfDaysIntoFuture = 28;
    toDate = new Date(toDate.getTime() + (numberOfDaysIntoFuture * 24 * 60 * 60 * 1000));

    try {
      this.calendar.calendarGet(fromDate, toDate, this.addressSelected.countryCode).subscribe(
        (result) => {
          result.calendarList?.forEach(c => c.calendarDate.setUTCHours(12, 0, 0, 0));

          let allowedDeliveryDay = this.allowedDeliveryDayGet(result.calendarList, this.bsConfig);
          this.logger.info(`calendarListGet allowedDeliveryDay: ${allowedDeliveryDay?.calendarDate.toString()}`, this.constructor.name);

          let calculatedUTCOffsetMillis = this.utcOffsetInMillisGet(now);
          this.logger.info(`calendarListGet calculatedUTCOffsetMillis: ${calculatedUTCOffsetMillis}`, this.constructor.name);

          this.calendarDayList.forEach(
            (calCellCurrent) => {
              if (calCellCurrent == undefined) {
                return;
              }

              let calIndex = result.calendarList?.findIndex(
                (c) => {
                  let utcCalDateWithOffset = this.utcDateTimeGet(c.calendarDate, calculatedUTCOffsetMillis);
                  return this.comparisonService.equalsDateByValues(calCellCurrent.date, utcCalDateWithOffset);
                });
              this.logger.info(`calendarListGet calIndex: ${calIndex}`, this.constructor.name);

              if (calIndex != undefined && calIndex >= 0) {
                let cal = result.calendarList?.[calIndex];
                this.logger.info(`calendarListGet cal: ${cal?.calendarDate.toString()}`, this.constructor.name);

                if (cal?.isHoliday || cal?.isWorkday != true) {
                  calCellCurrent.isAvailable = false;

                  if (cal?.isHoliday) { calCellCurrent.dayType = EDayType.Holiday; }

                  this.logger.info(`calendarListGet case 0`, this.constructor.name);
                }
                else {
                  let calCellCurrentTempDate = this.utcDateTimeGet(cal.calendarDate, calculatedUTCOffsetMillis);
                  if (allowedDeliveryDay?.calendarDate != undefined && this.comparisonService.biggerEqualsDateByValues(calCellCurrentTempDate, allowedDeliveryDay?.calendarDate)) {
                    calCellCurrent.isAvailable = true;
                    this.logger.info(`calendarListGet case 1`, this.constructor.name);
                  }
                  else if (allowedDeliveryDay == undefined && calCellCurrent.date > now) {
                    calCellCurrent.isAvailable = true;
                    this.logger.info(`calendarListGet case 2`, this.constructor.name);
                  }
                  else {
                    calCellCurrent.isAvailable = false;
                    this.logger.info(`calendarListGet case 3`, this.constructor.name);
                  }
                }
              }

              if (calCellCurrent.isAvailable == true) {
                calCellCurrent.isAvailable = this.dayIsInTourList(this.addressSelected?.tourList, calCellCurrent.date.getDay());
                this.logger.info(`calendarListGet case 4`, this.constructor.name);
              }
            }
          );
        }
      );
    } catch (error) {
      // ToDo: print toast / error
    }

  }

  private allowedDeliveryDayGet(
    calendarList: CalendarDto[] | undefined,
    bsConfig: IBetriebsstaetteEnvironment | undefined
  ): CalendarDto | undefined {
    if (calendarList == undefined) { return undefined; }
    if (calendarList.length <= 0) { return undefined; }

    // let offSetWorkdays = 0;
    // default configuration for no specific bs and no specific weekday
    let delDayCutoffTime = ENVIRONMENT.checkout.defaultCommissionTime;
    let delDayDayOffset = ENVIRONMENT.checkout.defaultCommissionDayOffset;


    let now = new Date();

    let cutoffTimeTimezone = this.serverTimeZone;
    this.logger.info(`allowedDeliveryDayGet cutoffTimeTimezone: ${cutoffTimeTimezone}`, this.constructor.name);

    let calculatedUTCOffsetHours = now.getTimezoneOffset() / 60;
    this.logger.info(`allowedDeliveryDayGet calculatedUTCOffsetHours: ${calculatedUTCOffsetHours}`, this.constructor.name);

    // lookup of the starting point. e.g. find the index of the desired day in the list
    let delDayIndex = calendarList.findIndex(
      (c) =>
        c.calendarDate.getDate() == now.getDate()
        && c.calendarDate.getMonth() == now.getMonth()
        && c.calendarDate.getFullYear() == now.getFullYear()
    );
    this.logger.info(`allowedDeliveryDayGet index: ${delDayIndex}`, this.constructor.name);

    // "now" is not in the calendar. is there a day which is greater? (e.g. we're looking at the future)
    if (delDayIndex == undefined || delDayIndex < 0) {
      delDayIndex = calendarList.findIndex(c => this.comparisonService.biggerEqualsTimeInMilli(c.calendarDate, now));
      this.logger.info(`allowedDeliveryDayGet index undefined: ${delDayIndex}`, this.constructor.name);
    }

    // "now" is not in the calendar. and not in the future. (e.g. we're looking at the past)
    if (delDayIndex == undefined || delDayIndex < 0) {
      this.logger.info(`allowedDeliveryDayGet index undefined: return`, this.constructor.name);
      return undefined;
    }

    // either "now" or the first day based on the found starting point is prepared as index. (e.g. "now" or "first day greater than now in the future")

    let delDayCalendar = undefined;
    let isDateOk: boolean = false;

    // while nothing found and not yet at the end of the posibilities in the calendarList
    while (!isDateOk && delDayIndex < calendarList.length) {
      // load the desired day and start the lookup and validation
      delDayCalendar = calendarList?.[delDayIndex];
      this.logger.info(`allowedDeliveryDayGet day: ${delDayCalendar?.calendarDate.toString()}`, this.constructor.name);

      let delDayDayOfWeek = delDayCalendar?.calendarDate.getDay();

      // load the config for this day
      let daySchedule = bsConfig?.commissiondayScheduleList?.find((bsDayConfig) => bsDayConfig.day == delDayDayOfWeek);
      if (daySchedule != undefined) {
        // specific configuration for this bs and weekday
        delDayCutoffTime = daySchedule.time;
        if (daySchedule.dayOffset != undefined) {
          delDayDayOffset = daySchedule.dayOffset;
        }
      } else {
        // fallback configuration for this bs but no weekday
        if (bsConfig?.defaultCommissionTime != undefined) {
          delDayCutoffTime = bsConfig?.defaultCommissionTime;
        }
        if (bsConfig?.defaultCommissionDayOffset != undefined) {
          delDayDayOffset = bsConfig?.defaultCommissionDayOffset;
        }
      }

      // ensure only valid / usable data is used
      if (delDayCutoffTime == undefined) {
        delDayCutoffTime = { hour: 12, minute: 0 };
      }
      if (delDayCutoffTime.hour < 0 || delDayCutoffTime.hour > 23) {
        delDayCutoffTime = { hour: 12, minute: delDayCutoffTime.minute };
      }
      if (delDayCutoffTime.minute < 0 || delDayCutoffTime.minute > 59) {
        delDayCutoffTime = { hour: delDayCutoffTime.hour, minute: 0 };
      }
      if (delDayDayOffset == undefined) {
        delDayDayOffset = 1;
      }
      if (delDayDayOffset < 0) {
        delDayDayOffset = Math.abs(delDayDayOffset);
      }


      // calculate the time for this day/configuration including the timezone and server/client offset.
      this.logger.info(`allowedDeliveryDayGet cutoffTime: ${delDayCutoffTime}`, this.constructor.name);
      let delDayCutoffTimeUTC = delDayCutoffTime.hour - cutoffTimeTimezone;
      this.logger.info(`allowedDeliveryDayGet cutoffTimeUTC: ${delDayCutoffTimeUTC}`, this.constructor.name);
      let delDayCalculatedLocalCutOffHours = delDayCutoffTimeUTC - calculatedUTCOffsetHours;
      if (this.isDaylightSavingTime()) {
        this.logger.info(`allowedDeliveryDayGet DaylightSavingTime detected`, this.constructor.name);
        delDayCalculatedLocalCutOffHours = delDayCalculatedLocalCutOffHours - 1;
      }
      this.logger.info(`allowedDeliveryDayGet calculatedLocalCutOffHours: ${delDayCalculatedLocalCutOffHours}`, this.constructor.name);

      let relComDayIndex = delDayIndex;
      let relComDayCalendar = calendarList?.[relComDayIndex]; // init the relComDayCalendar in case we're starting with 0 days offset -> while loop is skipped
      let relComDayCalendarDate: Date | undefined = relComDayCalendar?.calendarDate;
      let relComDay: Date | undefined = new Date(relComDayCalendarDate.getFullYear(), relComDayCalendarDate.getMonth(), relComDayCalendarDate.getDate(), delDayCalculatedLocalCutOffHours, delDayCutoffTime.minute, 0);

      // inner relDay offset loop -> how many holidays / non workdays do i need to skip to reach the next workday?
      while (relComDayIndex > 1 && (relComDayCalendar?.isHoliday || relComDayCalendar?.isWorkday != true)) {
        relComDayIndex--;
        relComDayCalendar = calendarList?.[relComDayIndex];
      }

      if (relComDayIndex == 0 && (relComDayCalendar?.isHoliday || relComDayCalendar?.isWorkday != true)) {
        // no valid day reached and end of index -> abort the back traversing of relComDay -> continue with the forward traversing of delDays
        continue;
      }

      // loop for traveling backwards from the commission day based on the configuration (dayOffset) and not yet reached the lowest index (e.g. start of the calendarList)
      while (delDayDayOffset > 0 && relComDayIndex > 1) {
        relComDayIndex--;
        delDayDayOffset--;

        relComDayCalendar = calendarList?.[relComDayIndex];
        // inner relDay offset loop -> how many holidays / non workdays do i need to skip to reach the next workday?
        while (relComDayIndex > 1 && (relComDayCalendar?.isHoliday || relComDayCalendar?.isWorkday != true)) {
          relComDayIndex--;
          relComDayCalendar = calendarList?.[relComDayIndex];
        }

        if (relComDayIndex == 0 && (relComDayCalendar?.isHoliday || relComDayCalendar?.isWorkday != true)) {
          // no valid day reached and end of index -> abort the back traversing of relComDay -> continue with the forward traversing of delDays
          relComDayCalendarDate = undefined;
          relComDay = undefined;
          break;
        } else {
          // found a workday in the inner loop -> store it as a temporary result. Let the outer loop continue. either it terminates and takes the temporary result as a final result or it starts the next loop.
          relComDayCalendarDate = relComDayCalendar?.calendarDate;
          if (relComDayCalendarDate == undefined) {
            this.logger.error("Failed to extract the date object from the calendar entry.", this.constructor.name);
          } else {
            relComDay = new Date(relComDayCalendarDate.getFullYear(), relComDayCalendarDate.getMonth(), relComDayCalendarDate.getDate(), delDayCalculatedLocalCutOffHours, delDayCutoffTime.minute, 0);
          }
        }
      }

      if (delDayDayOffset > 0) {
        // Exited the relComDay backtraversing loop with offset remaining... Invalid state -> clear the temporary result -> let the delDay forwardtraversing loop continue
        isDateOk = false;
        delDayIndex++;
        delDayCalendar = undefined;
        continue;
      }

      if (relComDay == undefined) {
        // Exited the relComDay backtraversing loop without a result... Invalid state -> clear the temporary result -> let the delDay forwardtraversing loop continue
        isDateOk = false;
        delDayIndex++;
        delDayCalendar = undefined;
        continue;
      }

      // Exited the relComDay backtraversing loop with a result... success -> use it
      let calculatedCutOffDateTime = new Date(relComDay?.getFullYear(), relComDay?.getMonth(), relComDay?.getDate(), delDayCalculatedLocalCutOffHours, delDayCutoffTime.minute, 0);
      this.logger.warn(`allowedDeliveryDayGet calculatedCutOffDateTime: ${calculatedCutOffDateTime.toString()}`, this.constructor.name);

      // Start the next validation of the found relComDay... e.g. compare it to "now"
      if (now <= calculatedCutOffDateTime) {
        // relComDay is in future -> delDay is allowed -> day is found. end the forwardtraversing loop.
        isDateOk = true;
        break;
      } else {
        // relComDay is in the past -> delDay is not allowed -> increase the delDayIndex, clear the temp data and let the delDay forwarstraversing loop continue.
        isDateOk = false;
        delDayIndex++;
        delDayCalendar = undefined;
        continue;
      }

      // end of outer outer loop which iterates 1 delivery day into the future until a valid day is found... or the index is no longer in the calendarList
    }

    return isDateOk == true ? delDayCalendar : undefined;
  }

  private utcOffsetInMillisGet(dateTime: Date) {
    let calculatedUTCOffsetHours = dateTime.getTimezoneOffset() / 60;
    let calculatedUTCOffsetMillis = calculatedUTCOffsetHours * 60 * 60 * 1000;
    return calculatedUTCOffsetMillis;
  }

  private utcDateTimeGet(dateTime: Date, calculatedUTCOffsetMillis: number | undefined = undefined) {
    if (calculatedUTCOffsetMillis == undefined) {
      calculatedUTCOffsetMillis = this.utcOffsetInMillisGet(dateTime);
    }
    return new Date(dateTime.getTime() - calculatedUTCOffsetMillis);
  }

  private isDaylightSavingTime() {
    let now = new Date();
    let jan = new Date(now.getFullYear(), 0, 1);
    let jul = new Date(now.getFullYear(), 6, 1);
    let maxTimeZoneOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset()); // both values are in europe negative --> smaller offset is extracted
    let isDaylightSavingTimeActive = now.getTimezoneOffset() < maxTimeZoneOffset;
    return isDaylightSavingTimeActive;
  }

}

export class CalendarDayModel {
  constructor(
    public date: Date,
    public dayOfMonth: number,
    public dayOfWeek: EDayOfWeek,
    public isSelected: boolean = false,
    public isAvailable: boolean = false,
    public dayType: EDayType = EDayType.Default,
    public display: boolean = true
  ) {
  }
}
